import {
  Box,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  ListItem,
  Switch,
  Text,
  Textarea,
  UnorderedList,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";

import { Button } from "../../components";
import useSelectTheme from "../../hooks/useSelectTheme";
import CoachingPreviewModal from "./CoachingPreviewModal";
import { CoachingConfiguration, TaskRunResult, UserOption } from "./types";

type CoachingFormProps = {
  configuration: CoachingConfiguration;
  taskRunResult: TaskRunResult;
  onPreview: (userId: string, config: CoachingConfiguration) => void;
  onPreviewModalClose?: () => void;
  onSave: (config: CoachingConfiguration) => void;
  saveLoading?: boolean;
  userOptions: UserOption[];
  userOptionsLoading: boolean;
  onUserSearchInputChange?: (value: string, actionMeta: any) => void;
  initialSelectedUsers?: UserOption[];
  initialUsersLoading?: boolean;
  disableEditing?: boolean;
  enableLabel?: string;
};

const personaNiceLabels: Record<string, string> = {
  INTERVIEWER: "Interviewer",
  COORDINATOR_OPS: "Recruiting Coordinator/Operations",
  RECRUITER: "Recruiter",
  SOURCER: "Sourcer",
  HIRING_MANAGER: "Hiring Manager",
  TA_LEADER: "Executive/TA Leader",
};

const personaOptions = Object.keys(personaNiceLabels).map((value) => ({
  label: personaNiceLabels[value],
  value,
}));

const CoachingForm: React.FC<CoachingFormProps> = ({
  configuration,
  taskRunResult,
  onPreview,
  onPreviewModalClose,
  onSave,
  saveLoading = false,
  userOptions,
  userOptionsLoading,
  onUserSearchInputChange,
  initialSelectedUsers,
  initialUsersLoading = false,
  disableEditing = false,
  enableLabel = "Interview coaching tips will be sent to your selected interviewers every two weeks on Monday.",
}) => {
  const [previewUserId, setPreviewUserId] = useState<string | undefined>(
    undefined
  );
  const previewModal = useDisclosure();

  const { register, handleSubmit, control, setValue, watch } =
    useForm<CoachingConfiguration>({
      defaultValues: {
        ...configuration,
      },
    });

  const watchValues = watch();
  const watchFilter = watch("filter");

  const onPreviewClick = useCallback(() => {
    if (!previewUserId || disableEditing) {
      return;
    }

    previewModal.onOpen();
    onPreview(previewUserId, watchValues);
  }, [disableEditing, onPreview, previewModal, previewUserId, watchValues]);

  const saveChanges = handleSubmit((values) => {
    onSave(values);
  });

  const closePreviewModal = useCallback(() => {
    previewModal.onClose();
    onPreviewModalClose?.();
  }, [onPreviewModalClose, previewModal]);

  const [theme, styles] = useSelectTheme({
    container: (provided: Record<string, any>) => {
      return { ...provided, flex: 1 };
    },
    control: (provided: Record<string, any>) => {
      return {
        ...provided,
        borderRadius: "0.375rem",
        borderColor: "inherit",
        fontSize: "14px",
      };
    },
  });

  const filterTypeOptions = [
    { label: "All interviewers", value: "all" },
    { label: "Specific interviewers", value: "interviewer" },
    { label: "Persona", value: "persona" },
  ];

  const selectedFilterType = useMemo(() => {
    if (watchFilter?.type === "PERSONA") {
      return filterTypeOptions[2];
    }

    if (watchFilter?.type === "USER") {
      return filterTypeOptions[1];
    }

    return filterTypeOptions[0];
  }, [watchFilter]);

  return (
    <>
      <form onSubmit={saveChanges}>
        <Box mb={8}>
          <Heading as="h3" size="sm" mb={2}>
            Delivery
          </Heading>
          <Text fontSize="sm" fontWeight="normal" color="gray.600" mb={2}>
            Control whether your interviewers receive coaching tips.
          </Text>
          <FormControl display="flex" alignItems="start" mt="2">
            <Switch
              id="sendCoachingTips"
              isDisabled={disableEditing}
              defaultChecked={configuration.sendEmail}
              {...register("sendEmail")}
            />
            <FormLabel
              htmlFor="sendCoachingTips"
              mb="0"
              ml="3"
              color="gray.900"
              fontSize="sm"
            >
              {enableLabel}
            </FormLabel>
          </FormControl>
        </Box>

        <Divider my={6} />

        <Box mb={8}>
          <Heading as="h3" size="sm" mb={2} mt={4}>
            Audience
          </Heading>
          <Text fontSize="sm" fontWeight="normal" color="gray.600" mb={2}>
            Choose which interviewers will receive coaching tips.
          </Text>
          <Flex maxW="300px" mb={2}>
            <Controller
              name="filter"
              control={control}
              render={({ field }) => (
                <Select
                  value={selectedFilterType}
                  options={filterTypeOptions}
                  isDisabled={disableEditing}
                  onChange={(option) => {
                    if (option?.value === "persona") {
                      field.onChange({ type: "PERSONA", values: [] });
                      return;
                    }
                    if (option?.value === "interviewer") {
                      field.onChange({ type: "USER", values: [] });
                      return;
                    }
                    field.onChange(null);
                  }}
                  isClearable={false}
                  styles={styles}
                  theme={theme}
                />
              )}
            />
          </Flex>

          {watchFilter?.type === "PERSONA" && (
            <FormControl display="flex" alignItems="center" mt="2" mb="2">
              <Flex flex={1}>
                <Controller
                  name="filter.values"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      isMulti
                      isDisabled={disableEditing}
                      theme={theme}
                      styles={styles}
                      value={(field.value || []).map((persona) => ({
                        label: personaNiceLabels[persona],
                        value: persona,
                      }))}
                      options={personaOptions}
                      onChange={(personas) => {
                        setValue(
                          "filter.values",
                          personas ? personas.map((p) => p.value) : []
                        );
                      }}
                    />
                  )}
                />
              </Flex>
            </FormControl>
          )}

          {watchFilter?.type === "USER" && (
            <UserMultiSelect
              control={control}
              setValue={setValue}
              userOptions={userOptions}
              userOptionsLoading={userOptionsLoading}
              onUserSearchInputChange={onUserSearchInputChange}
              initialSelectedUsers={initialSelectedUsers}
              initialUsersLoading={initialUsersLoading}
              disableEditing={disableEditing}
              theme={theme}
              styles={styles}
            />
          )}
        </Box>

        <Divider my={6} />

        <Box mb={8}>
          <Heading as="h3" size="sm" mb={2}>
            Coaching content
          </Heading>
          <UnorderedList
            fontSize="sm"
            fontWeight="normal"
            color="gray.800"
            mb={2}
          >
            <ListItem>
              The default metrics are: Candidate Talk Ratio, Question
              Opportunity, Agenda Set and On-time Starts.
            </ListItem>
            <ListItem>
              The default topics are: Follow-up Questions, Leading Questions,
              Question Delivery, Acknowledging Answers, and Representing The
              Company.
            </ListItem>
          </UnorderedList>

          <Text fontSize="sm" fontWeight="normal" color="gray.600" mb={2}>
            Customization instructions
          </Text>

          <Textarea
            minH="130px"
            fontSize="sm"
            isDisabled={disableEditing}
            placeholder="Describe any changes to the default format and content. For example,&#10;&#x2022;Skip the metrics, and show just one coaching topic. &#10;&#x2022;Add a coaching topic about how to sell Example Co. to candidates.&#10;&#x2022;Add a coaching topic about compliance with hiring practices in Example Country&#10;&#x2022;Ignore the defaults and only output a single paragraph of feedback to the interviewer."
            _placeholder={{ color: "gray.400", lineHeight: "20px" }}
            borderColor="gray.200"
            {...register("customInstructions")}
          />
        </Box>

        <Divider my={6} />

        <Box mb={8}>
          <Heading as="h3" size="sm" mb={2}>
            Preview
          </Heading>
          <Text fontSize="sm" fontWeight="normal" color="gray.600" mb={2}>
            Test out what your coaching tips will look like for a specific user.
          </Text>
          <Flex direction="row" mt="4" mb="4">
            <Select
              theme={theme}
              styles={styles}
              isClearable
              placeholder="Select a User"
              isLoading={userOptionsLoading}
              isDisabled={disableEditing}
              filterOption={onUserSearchInputChange ? () => true : undefined}
              onInputChange={onUserSearchInputChange}
              options={userOptions}
              onChange={(option) =>
                setPreviewUserId(
                  (option as { value: string } | undefined)?.value
                )
              }
            />
            <Button
              variant="prismSecondary"
              size="md"
              ml="4"
              onClick={onPreviewClick}
              isDisabled={!previewUserId || disableEditing}
            >
              Preview
            </Button>
          </Flex>
        </Box>

        <Box mb={8}>
          <Button
            variant="prismPrimary"
            mt="7"
            type="submit"
            isDisabled={disableEditing}
            isLoading={saveLoading}
            loadingText="Saving..."
          >
            Save changes
          </Button>
        </Box>
      </form>

      <CoachingPreviewModal
        isOpen={previewModal.isOpen}
        onClose={closePreviewModal}
        taskRunResult={taskRunResult}
      />
    </>
  );
};

type UserMultiSelectProps = {
  control: any;
  setValue: any;
  userOptions: UserOption[];
  userOptionsLoading: boolean;
  onUserSearchInputChange?: (value: string, actionMeta: any) => void;
  initialSelectedUsers?: UserOption[];
  initialUsersLoading?: boolean;
  disableEditing: boolean;
  theme: any;
  styles: any;
};

const UserMultiSelect: React.FC<UserMultiSelectProps> = ({
  control,
  setValue,
  userOptions,
  userOptionsLoading,
  onUserSearchInputChange,
  initialSelectedUsers,
  initialUsersLoading = false,
  disableEditing,
  theme,
  styles,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<UserOption[]>(
    initialSelectedUsers ?? []
  );

  useEffect(() => {
    if (initialSelectedUsers && initialSelectedUsers.length > 0) {
      setSelectedOptions((prev) =>
        prev.length === 0 ? initialSelectedUsers : prev
      );
    }
  }, [initialSelectedUsers]);

  return (
    <FormControl display="flex" alignItems="center" mt="2" mb="2">
      <Flex flex={1}>
        <Controller
          name="filter.values"
          control={control}
          render={() => (
            <Select
              isMulti
              isDisabled={disableEditing}
              isLoading={userOptionsLoading || initialUsersLoading}
              filterOption={onUserSearchInputChange ? () => true : undefined}
              onInputChange={onUserSearchInputChange}
              theme={theme}
              styles={styles}
              value={selectedOptions}
              options={userOptions}
              onChange={(users) => {
                const selected = Array.isArray(users) ? users : [];
                setSelectedOptions(selected);
                setValue(
                  "filter.values",
                  selected.map((option) => option.value)
                );
              }}
            />
          )}
        />
      </Flex>
    </FormControl>
  );
};

export default CoachingForm;
